/**
 * Emergent Marketplace — Phase 5
 * ProductDetail.jsx
 * Architectural Layer: Presentation → Marketplace
 *
 * Full product page. Works for all 4 types.
 * Physical: gallery, shipping info, add to cart.
 * Digital: what's included, instant access after purchase.
 * Course: curriculum preview, duration, add to cart.
 * Session: duration/format, links to booking flow.
 * Design: Clean luxury — chalk white, deep forest, warm copper.
 * Font: Fraunces (display) + Cabinet Grotesk (UI)
 */

import { useState, useEffect, useRef } from "react";
import {
  ShoppingBag, Download, BookOpen, Calendar, Star,
  Check, Clock, Package, Truck, Shield, ChevronLeft, Hammer,
  ChevronRight, Plus, Minus, ArrowRight, Globe
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch } from "./api";
import ReturnPolicySummary from "@/components/marketplace/ReturnPolicySummary";
import { MarketplaceHeader } from "../../components/marketplace/MarketplaceHeader";
import { AddedToCartOverlay } from "../../components/marketplace/AddedToCartOverlay";
import { ExpandableText } from "../../components/marketplace/ExpandableText";
import "./MarketplaceHome.css";
import { useLanguage } from "@/contexts/LanguageContext";
import { isMobileBuild } from "@/lib/mobileBuild";
import UpgradeOnWebNotice from "@/components/UpgradeOnWebNotice";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Cabinet+Grotesk:wght@300;400;500;700&display=swap');

  .pd-root {
    --forest:      #1a3828;
    --forest-mid:  #2a5a40;
    --forest-light:#3a7a58;
    --copper:      #b85a28;
    --copper-light:#d87848;
    --copper-bg:   #fdf5ee;
    --copper-border:#e8c0a0;
    --chalk:       #fafaf8;
    --chalk-dark:  #f0ede8;
    --white:       #ffffff;
    --border:      #e4ddd8;
    --ink:         #1a1614;
    --ink-mid:     #5a5450;
    --ink-light:   #9a9490;
    --gold:        #b88820;
    font-family: 'Cabinet Grotesk', sans-serif;
    background: var(--chalk);
    color: var(--ink);
    min-height: 100vh;
  }

  /* ── Nav ─────────────────────────────────────────────────────────────── */
  .pd-nav {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 13px 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--ink-light);
  }

  .pd-nav-link {
    background: none; border: none; cursor: pointer;
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 13px; color: var(--ink-light);
    transition: color 0.15s;
  }

  .pd-nav-link:hover { color: var(--forest); }
  .pd-nav-sep { color: var(--border); }
  .pd-nav-current { color: var(--ink); font-weight: 500; }

  /* ── Main layout ─────────────────────────────────────────────────────── */
  .pd-layout {
    max-width: 900px;
    margin: 0 auto;
    padding: 28px 24px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: start;
  }

  /* Grid children default to min-width:auto, so a large photo's intrinsic width
     becomes the column's floor and blows the 1fr 1fr split apart. */
  .pd-layout > * { min-width: 0; }

  @media (max-width: 640px) {
    .pd-layout { grid-template-columns: 1fr; gap: 24px; }
  }

  /* ── Image gallery ───────────────────────────────────────────────────── */
  .pd-gallery {}

  .pd-main-image {
    aspect-ratio: 1;
    background: linear-gradient(135deg, var(--chalk-dark), #e8e0d8);
    border: 1px solid var(--border);
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    color: var(--ink-light);
    margin-bottom: 10px;
    overflow: hidden;
    position: relative;
  }

  .pd-main-image img {
    width: 100%; height: 100%;
    max-width: 100%;
    object-fit: cover;
  }

  .pd-type-overlay {
    position: absolute;
    top: 12px; left: 12px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    backdrop-filter: blur(6px);
  }

  .pd-overlay-physical { background: rgba(26,56,40,0.85); color: #a0e0c0; }
  .pd-overlay-digital  { background: rgba(26,56,40,0.85); color: #a0e0c0; }
  .pd-overlay-course   { background: rgba(184,88,40,0.85); color: #f8d0b0; }
  .pd-overlay-session  { background: rgba(96,48,160,0.85); color: #e0c8f8; }

  /* Wraps, because a listing with a dozen photos would otherwise set a min-content
     floor of ~900px on this column and crush the detail column beside it. */
  .pd-thumbnails {
    display: flex; flex-wrap: wrap; gap: 8px;
  }

  .pd-thumb {
    width: 64px; height: 64px;
    border-radius: 8px;
    background: var(--chalk-dark);
    border: 2px solid transparent;
    cursor: pointer;
    overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    color: var(--ink-light);
    transition: border-color 0.15s;
  }

  .pd-thumb.active { border-color: var(--forest); }

  /* ── Product info ────────────────────────────────────────────────────── */
  .pd-info {}

  .pd-vendor-link {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 700;
    color: var(--forest);
    margin-bottom: 8px;
    cursor: pointer;
    background: none; border: none;
    font-family: 'Cabinet Grotesk', sans-serif;
    transition: opacity 0.15s;
  }

  .pd-vendor-link:hover { opacity: 0.75; }

  .pd-title {
    font-family: 'Fraunces', serif;
    font-size: 28px; font-weight: 400;
    color: var(--ink);
    margin: 0 0 10px;
    line-height: 1.25;
  }

  .pd-rating-row {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px;
    margin-bottom: 16px;
  }

  .pd-stars { display: flex; gap: 2px; }

  .pd-rating-avg { font-weight: 700; color: var(--gold); }
  .pd-rating-count { color: var(--ink-light); }

  .pd-price {
    font-family: 'Fraunces', serif;
    font-size: 32px; font-weight: 400;
    color: var(--forest);
    margin-bottom: 16px;
  }

  .pd-fulfil-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 14px;
  }

  .pd-fulfil-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .pd-fulfil-badge.mto  { background: var(--copper-bg); color: var(--copper); }
  .pd-fulfil-badge.free { background: #e8f5e9; color: #1a7a4a; }

  .pd-description {
    font-size: 15px;
    color: var(--ink-mid);
    line-height: 1.75;
    margin-bottom: 20px;
    white-space: pre-line;
  }

  .pd-desc-toggle {
    display: block;
    margin: -10px 0 20px;
    background: none; border: none; padding: 0;
    font-family: inherit; font-size: 14px; font-weight: 600;
    color: var(--forest); cursor: pointer;
    text-decoration: underline; text-underline-offset: 3px;
    transition: opacity 160ms ease;
  }
  .pd-desc-toggle:hover { opacity: 0.65; }

  /* Feature list */
  .pd-features { margin-bottom: 20px; }

  .pd-feature {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 7px 0;
    border-bottom: 1px solid var(--chalk-dark);
    font-size: 14px; color: var(--ink-mid);
  }

  .pd-feature:last-child { border-bottom: none; }

  .pd-feature-icon {
    width: 24px; height: 24px;
    border-radius: 6px;
    background: var(--chalk-dark);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    color: var(--forest);
    margin-top: 1px;
  }

  /* Qty + Add to cart */
  .pd-purchase {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 18px;
    margin-bottom: 16px;
  }

  .pd-qty-row {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .pd-qty-label { font-size: 13px; font-weight: 700; color: var(--ink); }

  .pd-qty-control {
    display: flex; align-items: center; gap: 0;
    background: var(--chalk);
    border: 1px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
  }

  .pd-qty-btn {
    width: 34px; height: 34px;
    border: none; background: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--ink-mid);
    transition: background 0.1s;
  }

  .pd-qty-btn:hover { background: var(--chalk-dark); }
  .pd-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .pd-qty-val {
    min-width: 36px; text-align: center;
    font-size: 15px; font-weight: 700;
    color: var(--ink);
  }

  .pd-add-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    background: var(--forest);
    border: none;
    color: white;
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 15px; font-weight: 700;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: background 0.15s;
    margin-bottom: 8px;
  }

  .pd-add-btn:hover { background: var(--forest-mid); }

  /* Sticky buy bar — the same buy box, pinned, once you have scrolled past the real one */
  .pd-sticky-buy {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    z-index: 60;
    background: rgba(252,250,246,0.97);
    backdrop-filter: blur(10px);
    border-top: 1px solid #e4ddd8;
    box-shadow: 0 -6px 24px rgba(20,16,12,0.14);
    animation: pd-slide-up 0.22s ease;
  }
  @keyframes pd-slide-up { from { transform: translateY(100%) } to { transform: translateY(0) } }
  .pd-sticky-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .pd-sticky-thumb {
    width: 42px; height: 42px; border-radius: 9px; object-fit: cover; flex-shrink: 0;
  }
  .pd-sticky-text { flex: 1; min-width: 0; }
  .pd-sticky-title {
    font-size: 13.5px; font-weight: 600; color: #1a1614;
    margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .pd-sticky-variant { font-size: 11.5px; color: #9a9490; margin: 1px 0 0; }
  .pd-sticky-price {
    font-family: 'Fraunces', serif;
    font-size: 19px; color: #1a3828; margin: 0; flex-shrink: 0;
  }
  .pd-sticky-qty {
    display: flex; align-items: center; gap: 8px; flex-shrink: 0;
    border: 1px solid #e4ddd8; border-radius: 999px; padding: 3px 8px;
  }
  .pd-sticky-qty button {
    width: 22px; height: 22px; border-radius: 50%;
    border: 1px solid #e4ddd8; background: #fff;
    color: #1a1614; font-size: 14px; line-height: 1; cursor: pointer;
  }
  .pd-sticky-qty button:disabled { opacity: 0.4; cursor: not-allowed; }
  .pd-sticky-qty span { font-size: 13px; font-weight: 700; min-width: 12px; text-align: center; }
  .pd-sticky-btn {
    flex-shrink: 0;
    display: inline-flex; align-items: center; gap: 7px;
    background: #1a3828; color: #fff;
    border: none; border-radius: 999px;
    padding: 11px 20px;
    font-size: 14px; font-weight: 600; font-family: inherit;
    cursor: pointer;
    transition: background 0.2s;
  }
  .pd-sticky-btn:hover { background: #2a5a40; }
  .pd-sticky-btn:disabled { opacity: 0.55; }

  @media (max-width: 640px) {
    /* A phone has room for price, quantity and the button — the title is right up the page. */
    .pd-sticky-inner { padding: 9px 12px; gap: 10px; }
    .pd-sticky-thumb, .pd-sticky-text { display: none; }
    .pd-sticky-price { font-size: 18px; margin-right: auto; }
    .pd-sticky-btn { padding: 11px 16px; font-size: 13.5px; }
  }

  .pd-book-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6030a0, #8050c0);
    border: none;
    color: white;
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 15px; font-weight: 700;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: opacity 0.15s;
  }

  .pd-book-btn:hover { opacity: 0.9; }

  .pd-trust-row {
    display: flex; justify-content: center; gap: 16px;
    font-size: 11px; color: var(--ink-light);
    margin-top: 10px;
  }

  .pd-trust-item {
    display: flex; align-items: center; gap: 4px;
  }

  /* ── Reviews section ─────────────────────────────────────────────────── */
  .pd-reviews-section {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 24px 40px;
  }

  .pd-section-title {
    font-family: 'Fraunces', serif;
    font-size: 22px; font-weight: 400;
    color: var(--ink);
    margin: 0 0 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }

  .pd-review-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px 18px;
    margin-bottom: 10px;
  }

  .pd-review-head {
    display: flex; justify-content: space-between; margin-bottom: 8px;
  }

  .pd-review-author { font-size: 14px; font-weight: 700; color: var(--ink); }
  .pd-review-date   { font-size: 12px; color: var(--ink-light); }

  .pd-review-body {
    font-size: 14px; color: var(--ink-mid);
    line-height: 1.65; margin: 6px 0 0;
  }

  .pd-review-reply {
    margin-top: 10px;
    padding: 10px 14px;
    background: var(--chalk-dark);
    border-left: 3px solid var(--forest);
    border-radius: 0 8px 8px 0;
    font-size: 13px; color: var(--ink-mid); line-height: 1.55;
  }

  .pd-review-reply strong {
    display: block; font-size: 11px; font-weight: 700;
    color: var(--forest); letter-spacing: 0.08em;
    text-transform: uppercase; margin-bottom: 3px;
  }

  /* ── Added confirmation ──────────────────────────────────────────────── */
  .pd-added-banner {
    position: fixed;
    bottom: 24px; left: 50%;
    transform: translateX(-50%);
    background: var(--forest);
    color: white;
    padding: 12px 24px;
    border-radius: 50px;
    font-size: 14px; font-weight: 700;
    display: flex; align-items: center; gap: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    animation: pd-slideup 0.25s ease;
    z-index: 100;
    white-space: nowrap;
  }

  @keyframes pd-slideup {
    from { transform: translateX(-50%) translateY(20px); opacity: 0; }
    to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
  }

  /* ── Variant selector ──────────────────────────────────────────────── */
  .pd-variants {
    margin-bottom: 18px;
  }

  .pd-variants-label {
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 8px;
  }

  .pd-variant-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .pd-variant-btn {
    padding: 8px 16px;
    border: 1.5px solid var(--border);
    border-radius: 8px;
    background: var(--chalk);
    font-family: 'Cabinet Grotesk', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--ink-mid);
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pd-variant-btn:hover {
    border-color: var(--forest-light);
    color: var(--ink);
  }

  .pd-variant-btn.active {
    border-color: var(--forest);
    background: var(--white);
    color: var(--forest);
    font-weight: 700;
    box-shadow: 0 0 0 2px rgba(26,56,40,0.1);
  }

  .pd-variant-btn.out-of-stock {
    opacity: 0.45;
    cursor: not-allowed;
    text-decoration: line-through;
  }

  .pd-variant-thumb {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    object-fit: cover;
  }

  .pd-variant-stock {
    font-size: 11px;
    color: var(--ink-light);
    font-weight: 400;
  }

  .pd-variant-stock.low {
    color: var(--copper);
  }
`;

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProductDetail({ onAddToCart, onBookNow }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [qty, setQty]         = useState(1);
  const [added, setAdded]     = useState(false);
  const [activeThumb, setActiveThumb] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showAdded, setShowAdded] = useState(false);
  // Sticky buy bar: shows only once the real Add to cart has scrolled out of sight, so it never
  // covers anything you have not already seen. The decision to buy happens down in the
  // description and the reviews — that is exactly where the button used to be unreachable.
  const buyRef = useRef(null);
  const topRef = useRef(null);
  const [showStickyBuy, setShowStickyBuy] = useState(false);

  // Auto-translate title + description to the user's language (Session 37)
  const { language, translateContent } = useLanguage();
  const [tContent, setTContent] = useState({});
  useEffect(() => {
    if (!product || language === "en") { setTContent({}); return; }
    let active = true;
    translateContent([product.title, product.description].filter(Boolean))
      .then((m) => { if (active) setTContent(m); });
    return () => { active = false; };
  }, [product, language]);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    apiFetch(`/marketplace/products/${productId}`)
      .then(setProduct)
      .catch(() => {})
      .finally(() => setLoading(false));
    // Feeds the Recently Viewed tab, so a product you opened is findable again.
    apiFetch(`/marketplace/recently-viewed/${productId}`, { method: "POST" }).catch(() => {});
  }, [productId]);

  useEffect(() => {
    const el = buyRef.current;
    if (!el) return;
    // Two observers rather than a scroll listener: the dashboard scrolls an inner container,
    // so window.scrollY stays at 0 and any scroll-distance rule silently never fires.
    // The bar shows only when the real buy box is off-screen AND the top of the page has gone
    // by — so it never covers anything on first paint, and it steps aside the moment the real
    // buy box is back in view.
    const top = topRef.current;
    let buyVisible = true;
    let topVisible = true;
    const decide = () => setShowStickyBuy(!buyVisible && !topVisible);
    const ioBuy = new IntersectionObserver(([e]) => { buyVisible = e.isIntersecting; decide(); },
                                           { threshold: 0 });
    const ioTop = new IntersectionObserver(([e]) => { topVisible = e.isIntersecting; decide(); },
                                           { threshold: 0 });
    ioBuy.observe(el);
    if (top) ioTop.observe(top);
    return () => { ioBuy.disconnect(); ioTop.disconnect(); };
  }, [product, loading]);

  const addToCart = async () => {
    // Require variant selection for products with variants
    if (hasVariants && !selectedVariant) return;
    try {
      const body = { product_id: productId, qty };
      if (selectedVariant) {
        body.variant_name = selectedVariant.name;
      }
      await apiFetch("/cart/items", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setAdded(true);
      setTimeout(() => setAdded(false), 2500);
      window.dispatchEvent(new Event("cart-updated"));
      // Was a 2.5-second label and nothing else. Now: confirmation, the whole cart, more to browse.
      setShowAdded(true);
    } catch (e) { console.error(e); }
  };

  if (loading || !product) return (
    <div style={{ padding: 40, textAlign: "center", color: "#8b80a8" }}>
      {loading ? "Loading product..." : "Product not found"}
    </div>
  );

  const isPhysical = product.type === "physical";
  const isSession  = product.type === "session";
  const isDigitalGood = product.type === "digital" || product.type === "course";
  const hasVariants = product.variants?.length > 0;

  // Build gallery images: main images + variant images
  const allImages = [
    ...(product.images || []),
    ...(product.variants || []).filter(v => v.image_url).map(v => v.image_url)
  ];

  // Calculate effective price based on selected variant
  const effectivePrice = selectedVariant
    ? (product.price + (selectedVariant.price_adjustment || 0))
    : product.price;

  const TypeIcon = { physical: Package, digital: Download, course: BookOpen, session: Calendar }[product.type];

  // "2-3 weeks", or just "3 weeks" when the vendor gave one figure.
  const leadTimeLabel = (() => {
    const { lead_time_min: min, lead_time_max: max, lead_time_unit: unit } = product;
    if (!min && !max) return null;
    const u = unit || "weeks";
    if (min && max && max !== min) return `${min}-${max} ${u}`;
    const n = min || max;
    return `${n} ${n === 1 ? u.replace(/s$/, "") : u}`;
  })();

  return (
    <>
      <style>{styles}</style>
      {/* Same header as the rest of the Marketplace — this page used to be a dead end */}
      <MarketplaceHeader compact />
      <div className="pd-root">

        {/* Breadcrumb nav */}
        <div className="pd-nav">
          <button className="pd-nav-link" onClick={() => navigate("/dashboard/marketplace")}>Marketplace</button>
          <span className="pd-nav-sep">></span>
          <button className="pd-nav-link" onClick={() => navigate(`/dashboard/marketplace/store/${product.vendor_id}`)}>
            {product.vendor_name || "Store"}
          </button>
          <span className="pd-nav-sep">›</span>
          <span className="pd-nav-current">{product.title}</span>
        </div>

        {/* Main layout */}
        <div className="pd-layout">

          {/* Gallery */}
          <div className="pd-gallery" ref={topRef}>
            <div className="pd-main-image">
              {(allImages.length > 0) ? (
                <img src={allImages[activeThumb] || allImages[0]} alt={product.title} />
              ) : (
                <TypeIcon size={60} style={{ opacity: 0.15 }} />
              )}
              <span className={`pd-type-overlay pd-overlay-${product.type}`}>{product.type}</span>
            </div>
            {allImages.length > 1 && (
              <div className="pd-thumbnails">
                {allImages.map((url, i) => (
                  <div
                    key={i}
                    className={`pd-thumb ${activeThumb === i ? "active" : ""}`}
                    onClick={() => setActiveThumb(i)}
                  >
                    <img src={url} alt={`Thumbnail ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="pd-info">
            <button className="pd-vendor-link" onClick={() => onBack?.("vendor", product.vendor_id)}>
              {product.vendor_name} <ChevronRight size={13} />
            </button>

            <h1 className="pd-title">{tContent[product.title] || product.title}</h1>

            {/* No reviews yet means no rating row at all — an empty "0.0 (0 reviews)" reads
                like a bad score on a brand-new item. */}
            {(product.rating_count || 0) > 0 && (
            <div className="pd-rating-row" data-testid="pd-rating-row">
              <div className="pd-stars">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} size={15}
                    fill={n <= Math.round(product.rating_avg || 0) ? "#b88820" : "none"}
                    color={n <= Math.round(product.rating_avg || 0) ? "#b88820" : "#e4ddd8"}
                  />
                ))}
              </div>
              <span className="pd-rating-avg">{(product.rating_avg || 0).toFixed(1)}</span>
              <span className="pd-rating-count">({product.rating_count} reviews)</span>
            </div>
            )}

            <p className="pd-price">${Number(effectivePrice).toFixed(2)}</p>
            {(product.made_to_order || product.print_on_demand || product.free_shipping) && (
              <div className="pd-fulfil-badges" data-testid="pd-fulfilment-badges">
                {(product.made_to_order || product.print_on_demand) && (
                  <span className="pd-fulfil-badge mto" data-testid="pd-made-to-order-badge">
                    <Hammer size={11} />
                    {product.print_on_demand ? "Print on demand" : "Made to order"}
                    {leadTimeLabel && <> — ships in {leadTimeLabel}</>}
                  </span>
                )}
                {product.free_shipping && (
                  <span className="pd-fulfil-badge free" data-testid="pd-free-shipping-badge">
                    <Truck size={11} /> Free shipping
                  </span>
                )}
              </div>
            )}
            <ExpandableText
              text={tContent[product.description] || product.description}
              className="pd-description"
              toggleClassName="pd-desc-toggle"
              testId="pd-description"
            />

            {/* Variant selector */}
            {hasVariants && (
              <div className="pd-variants" data-testid="variant-selector">
                <p className="pd-variants-label">Select type</p>
                <div className="pd-variant-options">
                  {product.variants.map((v, i) => {
                    const isSelected = selectedVariant?.name === v.name;
                    const outOfStock = v.stock !== undefined && v.stock !== null && v.stock <= 0;
                    return (
                      <button
                        key={i}
                        className={`pd-variant-btn ${isSelected ? "active" : ""} ${outOfStock ? "out-of-stock" : ""}`}
                        onClick={() => {
                          if (outOfStock) return;
                          setSelectedVariant(isSelected ? null : v);
                          // Jump to variant image if it has one
                          if (!isSelected && v.image_url) {
                            const imgIdx = allImages.indexOf(v.image_url);
                            if (imgIdx >= 0) setActiveThumb(imgIdx);
                          }
                        }}
                        data-testid={`variant-btn-${v.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {v.image_url && (
                          <img className="pd-variant-thumb" src={v.image_url} alt={v.name} />
                        )}
                        <span>{v.name}</span>
                        {v.price_adjustment > 0 && <span style={{ fontSize: 11, color: 'var(--copper)' }}>+${Number(v.price_adjustment).toFixed(2)}</span>}
                        {v.price_adjustment < 0 && <span style={{ fontSize: 11, color: 'var(--copper)' }}>−${Math.abs(v.price_adjustment).toFixed(2)}</span>}
                        {outOfStock && <span className="pd-variant-stock">Out of stock</span>}
                        {!outOfStock && v.stock > 0 && v.stock <= 5 && (
                          <span className="pd-variant-stock low">Only {v.stock} left</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Feature list */}
            {product.features && (
              <div className="pd-features">
                {product.features.map((f, i) => {
                  const FIcon = f.icon;
                  return (
                    <div key={i} className="pd-feature">
                      <div className="pd-feature-icon"><FIcon size={13} /></div>
                      <span>{f.text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Purchase block */}
            {isMobileBuild() && isDigitalGood ? (
              <div className="pd-purchase">
                <UpgradeOnWebNotice
                  title="Get this on our website"
                  description="Digital items are purchased securely on our website. Buy there once, then access it right here in the app."
                  ctaLabel="View on Website"
                  path={typeof window !== "undefined" ? window.location.pathname : "/dashboard/marketplace"}
                  testId="product-digital-web-notice"
                />
              </div>
            ) : (
            <div className="pd-purchase" ref={buyRef}>
              {isPhysical && (
                <div className="pd-qty-row">
                  <span className="pd-qty-label">Quantity</span>
                  <div className="pd-qty-control">
                    <button className="pd-qty-btn" onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}>
                      <Minus size={14} />
                    </button>
                    <span className="pd-qty-val">{qty}</span>
                    <button className="pd-qty-btn" onClick={() => setQty(q => q + 1)}>
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              )}

              {isSession ? (
                <button className="pd-book-btn" onClick={() => onBookNow?.(product.vendor_id, product.id)}>
                  <Calendar size={16} /> Book this session <ArrowRight size={15} />
                </button>
              ) : (
                <button
                  className="pd-add-btn"
                  onClick={addToCart}
                  disabled={hasVariants && !selectedVariant}
                  style={hasVariants && !selectedVariant ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag size={16} />
                  {hasVariants && !selectedVariant
                    ? "Select a type first"
                    : isPhysical ? `Add ${qty} to cart` : "Add to cart"}
                </button>
              )}

              <div className="pd-trust-row">
                <div className="pd-trust-item"><Shield size={11} /> Secure checkout</div>
                {!isPhysical && <div className="pd-trust-item"><Check size={11} /> Instant access</div>}
                {isPhysical && (product.made_to_order || product.print_on_demand) ? (
                  <div className="pd-trust-item"><Hammer size={11} />
                    {leadTimeLabel ? `Ships in ${leadTimeLabel}` : "Made to order"}
                  </div>
                ) : isPhysical && (
                  <div className="pd-trust-item"><Truck size={11} /> Ships 2–4 days</div>
                )}
              </div>

              <ReturnPolicySummary
                vendorId={product.vendor_id}
                productReturnable={product.returnable !== false}
                productConsumable={product.consumable === true}
              />
            </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        {product.reviews?.length > 0 && (
          <div className="pd-reviews-section">
            <p className="pd-section-title">Customer reviews</p>
            {product.reviews.map(r => (
              <div key={r.id} className="pd-review-card">
                <div className="pd-review-head">
                  <span className="pd-review-author">{r.author}</span>
                  <span className="pd-review-date">{r.date}</span>
                </div>
                <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} size={13}
                      fill={n <= r.rating ? "#b88820" : "none"}
                      color={n <= r.rating ? "#b88820" : "#e4ddd8"}
                    />
                  ))}
                </div>
                <p className="pd-review-body">{r.body}</p>
                {r.reply && (
                  <div className="pd-review-reply">
                    <strong>Response from {product.vendor_name}</strong>
                    {r.reply}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Added to cart banner */}
        {added && (
          <div className="pd-added-banner">
            <Check size={16} /> Added to cart
          </div>
        )}
      </div>

      {showStickyBuy && !showAdded && (
        <div className="pd-sticky-buy" data-testid="sticky-buy-bar">
          <div className="pd-sticky-inner">
            {product.images?.[0] && (
              <img className="pd-sticky-thumb" src={product.images[0]} alt="" />
            )}
            <div className="pd-sticky-text">
              <p className="pd-sticky-title">{tContent[product.title] || product.title}</p>
              {selectedVariant && <p className="pd-sticky-variant">{selectedVariant.name}</p>}
            </div>
            <p className="pd-sticky-price" data-testid="sticky-buy-price">${Number(effectivePrice).toFixed(2)}</p>
            {isSession ? (
              <button className="pd-sticky-btn"
                onClick={() => onBookNow?.(product.vendor_id, product.id)}
                data-testid="sticky-book-btn">
                <Calendar size={15} /> Book
              </button>
            ) : (
              <>
                {isPhysical && (
                  <div className="pd-sticky-qty">
                    <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}
                      data-testid="sticky-qty-minus" aria-label="One fewer">−</button>
                    <span>{qty}</span>
                    <button onClick={() => setQty(q => q + 1)}
                      data-testid="sticky-qty-plus" aria-label="One more">+</button>
                  </div>
                )}
                <button className="pd-sticky-btn" onClick={addToCart}
                  disabled={hasVariants && !selectedVariant}
                  onClickCapture={() => {
                    if (hasVariants && !selectedVariant) buyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  data-testid="sticky-add-to-cart-btn">
                  <ShoppingBag size={15} />
                  {hasVariants && !selectedVariant ? "Choose a type" : "Add to cart"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showAdded && (
        <AddedToCartOverlay product={product} variantName={selectedVariant?.name}
          onClose={() => setShowAdded(false)} />
      )}
    </>
  );
}
